import type { DelayPair } from './types';
import { DEFAULT_DURATION_MS, MIN_DURATION_MS } from '../config/presets';

const PILLOW_MAX_MS = 65535;

export function clampU16(value: number): number {
  return Math.max(0, Math.min(Math.trunc(value), 65535));
}

export function delayPairMs(pair: DelayPair): number {
  const num = pair[0];
  const den = pair[1] === 0 ? 100 : pair[1];
  return (num / den) * 1000;
}

export function delayPairLabel(pair: DelayPair): string {
  const ms = delayPairMs(pair);
  if (ms >= 1000) {
    const sec = ms / 1000;
    const secS =
      Math.abs(sec - Math.round(sec)) < 1e-9 ? String(Math.round(sec)) : sec.toFixed(3);
    return `${pair[0]}/${pair[1]}（${secS}s）`;
  }
  return `${pair[0]}/${pair[1]}（${Math.round(ms)}ms）`;
}

export function clampDurationMs(
  value: number | string,
  defaultMs: number = DEFAULT_DURATION_MS,
): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return defaultMs;
  return Math.max(MIN_DURATION_MS, Math.min(Math.round(n), PILLOW_MAX_MS));
}

export function msToDelayPair(ms: number): DelayPair {
  const v = clampDurationMs(ms);
  return [v, 1000];
}

/**
 * 解析用户输入：
 * - "1000" / "1000ms" → (1000, 1000)
 * - "1/100" / "999/1" → 精确 num/den
 */
export function parseDelaySpec(
  text: string,
  defaultMs: number = DEFAULT_DURATION_MS,
): DelayPair {
  let s = (text || '').trim().toLowerCase().replace(/\s+/g, '');
  if (!s) return msToDelayPair(defaultMs);
  if (s.endsWith('ms')) s = s.slice(0, -2);
  if (s.includes('/')) {
    const [a, b] = s.split('/', 2);
    const num = clampU16(Math.round(Number(a)));
    const den = clampU16(Math.round(Number(b)));
    if (!Number.isFinite(Number(a)) || !Number.isFinite(Number(b))) {
      throw new Error(`无效 delay num/den: ${text}`);
    }
    if (num === 0 && den === 0) {
      throw new Error('delay_num 与 delay_den 不能同时为 0');
    }
    return [num, den];
  }
  return msToDelayPair(clampDurationMs(s, defaultMs));
}

/** 编码占位 duration（ms）；真实值随后 patch */
export function delayPairToPlaceholderMs(pair: DelayPair): number {
  const ms = delayPairMs(pair);
  if (ms <= 0) return MIN_DURATION_MS;
  return Math.max(MIN_DURATION_MS, Math.min(Math.round(ms), PILLOW_MAX_MS));
}
